import { LicenceId } from '@ps/platform-message-framework/common/LicenceId';
import { ResolverTag } from '@ps/web-core-enums';
import type { Currency } from '@ps/web-core-enums';
import { Translation } from '@ps/web-shared-components';
import React from 'react';

import CurrencyAmount from '@swf/components/CurrencyAmount/CurrencyAmount';
import type { CustomDate } from '@swf/components/FormattedTimeZone/FormattedTimeZone';
import FormattedTimeZone from '@swf/components/FormattedTimeZone/FormattedTimeZone';
import ResolverLink from '@swf/components/ResolverLink/ResolverLink';
import LicensedTranslation from '@swf/components/Translation/LicensedTranslation';
import useWizardContext from '@swf/core/hooks/useWizardContext';
import type { InstantBonusInfo } from '@swf/first';

import BonusAmountIcon from '../../../../../assets/icons/bonus-amount.svg';
import BonusContributedIcon from '../../../../../assets/icons/bonus-contributed.svg';
import BonusTotalIcon from '../../../../../assets/icons/bonus-total.svg';
import {
  convertAmountByRate,
  getConversionRate,
  isZeroExponentCurrency,
  trimWithoutRounding
} from '../../../../../utils/cashier';

import {
  TXTACL_Bonus_Amount_XN0X,
  TXTACL_Contributed_Amount_XN0X,
  TXTCLI_ExpiresX_N0,
  TXTWCL_Any_deposit_amount_above_this__ELL,
  TXTWCL_At_any_timeX_you_may_surrender_ELL,
  TXTWCL_By_confirming_this_offerX_youX_ELL,
  TXTWCL_Maximum_bet_amountX_N0,
  TXTWCL_Maximum_bonus_winningsX_N0,
  TXTWCL_Once_your_deposit_is_completed_ELL_1,
  TXTWCL_Once_your_deposit_is_completed_ELL_2,
  TXTWCL_To_release_the_Bonus_Package_a_ELL,
  TXTWCL_Total_Bonus_Package_XN0X,
  TXTWCL_XThe_exchange_rate_from_N0_to__ELL,
  TXTWCL_Your_deposit_amountX_N0_N1,
  TXTWCL_Your_wagering_and_winnings_wil_ELL
} from './SplitWager.messages.json';
import {
  description,
  info,
  infoAmount,
  infoIcon,
  infoList,
  infoNote,
  infoRatioStatement,
  infoSegment,
  infoSign,
  name,
  summary
} from './SplitWager.scss';

interface Props {
  bonusName?: string;
  instantBonusInfo: InstantBonusInfo;
  bonusEnd: CustomDate;
  potentialBonusAmount?: number;
  balanceAmount?: number;
}

const SplitWager: React.FC<Props> = ({
  bonusName,
  bonusEnd,
  instantBonusInfo,
  potentialBonusAmount = 0,
  balanceAmount = 0
}) => {
  const {
    values: { deposit },
    data: {
      paySystemDetails: {
        rates: { curr: balanceCurrency, rates }
      }
    }
  } = useWizardContext();

  const { currency, amount } = deposit;
  const {
    bonusCurrency,
    bonusAmountInDepositCurrency,
    bonusAmountInBonusCurrency,
    depositCurrency,
    pointsToClear,
    cashPrice,
    cashPriceCurrency,
    urRedemptionLimit = 0,
    gameMaxBetInAccountCurrency = 0
  } = instantBonusInfo;

  const isCashPriceCurrencyZeroExponent = isZeroExponentCurrency(cashPriceCurrency as Currency);
  const isBonusCurrencyZeroExponent = isZeroExponentCurrency(bonusCurrency as Currency);

  const conversionRate = getConversionRate(rates, currency);
  const contributedCurrencyAmount = (
    <CurrencyAmount
      value={cashPrice}
      isZeroExponentCurrency={isCashPriceCurrencyZeroExponent}
      currency={cashPriceCurrency as Currency}
      style="currency"
    />
  );

  const contributedAmount = Math.round((cashPrice / bonusAmountInBonusCurrency) * 10000);
  const contributedAmountPercentage = contributedAmount / 100;
  const bonusAmountPercentage = (10000 - contributedAmount) / 100;
  const redemptionPoints = pointsToClear / 100.0;
  const hasMaxBonus = !!urRedemptionLimit;
  const allDepositForBonus = balanceAmount <= potentialBonusAmount;

  return (
    <>
      <div className={summary}>
        <Translation
          tagName="p"
          {...TXTWCL_Your_deposit_amountX_N0_N1}
          values={{
            0: (
              <b>
                <CurrencyAmount
                  value={trimWithoutRounding(amount)}
                  isZeroExponentCurrency={isZeroExponentCurrency(currency)}
                  currency={currency}
                  style="currency"
                  dollarValue
                />
              </b>
            ),
            1:
              currency !== balanceCurrency ? (
                <b>
                  (
                  <CurrencyAmount
                    value={convertAmountByRate(amount, conversionRate, balanceCurrency, false)}
                    isZeroExponentCurrency={isZeroExponentCurrency(balanceCurrency)}
                    currency={balanceCurrency}
                    style="currency"
                    dollarValue
                  />
                  )*
                </b>
              ) : null
          }}
        />
      </div>

      <div className={description}>
        <p>
          <Translation
            {...(allDepositForBonus
              ? TXTWCL_Once_your_deposit_is_completed_ELL_2
              : TXTWCL_Once_your_deposit_is_completed_ELL_1)}
            values={{
              0: contributedCurrencyAmount
            }}
          />
          {!allDepositForBonus && (
            <>
              {' '}
              <Translation {...TXTWCL_Any_deposit_amount_above_this__ELL} />
            </>
          )}
        </p>

        <p className={name}>{bonusName}</p>
        <div className={info}>
          <p className={infoSegment}>
            <BonusAmountIcon className={infoIcon} />
            <b className={infoAmount}>{contributedCurrencyAmount}</b>
            <Translation
              {...TXTACL_Contributed_Amount_XN0X}
              values={{ 0: `${contributedAmountPercentage}%` }}
            />
          </p>
          <span className={infoSign}>+</span>
          <p className={infoSegment}>
            <BonusContributedIcon className={infoIcon} />
            <b className={infoAmount}>
              <CurrencyAmount
                value={bonusAmountInDepositCurrency - cashPrice}
                isZeroExponentCurrency={isCashPriceCurrencyZeroExponent}
                currency={cashPriceCurrency as Currency}
                style="currency"
              />
            </b>
            <Translation
              {...TXTACL_Bonus_Amount_XN0X}
              values={{ 0: `${bonusAmountPercentage}%` }}
            />
          </p>
          <span className={infoSign}>=</span>
          <p className={infoSegment}>
            <BonusTotalIcon className={infoIcon} />
            <b className={infoAmount}>
              <CurrencyAmount
                value={bonusAmountInDepositCurrency}
                isZeroExponentCurrency={isZeroExponentCurrency(depositCurrency as Currency)}
                currency={depositCurrency as Currency}
                style="currency"
              />
            </b>
            <Translation {...TXTWCL_Total_Bonus_Package_XN0X} values={{ 0: '100%' }} />
          </p>
          <p className={infoRatioStatement}>
            <Translation
              {...TXTWCL_Your_wagering_and_winnings_wil_ELL}
              values={{
                0: `${contributedAmountPercentage}%`,
                1: `${bonusAmountPercentage}%`
              }}
            />
          </p>
        </div>
        <ul className={infoList}>
          <Translation
            tagName="li"
            {...TXTWCL_To_release_the_Bonus_Package_a_ELL}
            values={{ 0: <b>{redemptionPoints}</b> }}
          />
          {!!gameMaxBetInAccountCurrency && (
            <Translation
              tagName="li"
              {...TXTWCL_Maximum_bet_amountX_N0}
              values={{
                0: (
                  <b>
                    <CurrencyAmount
                      value={gameMaxBetInAccountCurrency}
                      isZeroExponentCurrency={isBonusCurrencyZeroExponent}
                      currency={bonusCurrency as Currency}
                      style="currency"
                    />
                  </b>
                )
              }}
            />
          )}
          <Translation
            tagName="li"
            {...TXTCLI_ExpiresX_N0}
            values={{
              0: (
                <b>
                  <FormattedTimeZone
                    date={bonusEnd}
                    year="numeric"
                    day="numeric"
                    month="long"
                    hour="numeric"
                    minute="numeric"
                    hour12={false}
                    timeZoneName="short"
                  />
                </b>
              )
            }}
          />
          <Translation tagName="li" {...TXTWCL_At_any_timeX_you_may_surrender_ELL} />
          {hasMaxBonus && (
            <Translation
              tagName="li"
              {...TXTWCL_Maximum_bonus_winningsX_N0}
              values={{
                0: (
                  <b>
                    <CurrencyAmount
                      value={urRedemptionLimit}
                      isZeroExponentCurrency={isBonusCurrencyZeroExponent}
                      currency={bonusCurrency as Currency}
                      style="currency"
                    />
                  </b>
                )
              }}
            />
          )}
        </ul>
        {currency !== balanceCurrency && (
          <p className={infoNote}>
            <Translation
              {...TXTWCL_XThe_exchange_rate_from_N0_to__ELL}
              values={{ 0: currency, 1: balanceCurrency }}
            />
          </p>
        )}
        <LicensedTranslation
          include={[LicenceId.UK]}
          tagName="p"
          {...TXTWCL_By_confirming_this_offerX_youX_ELL}
          values={{
            a: (msg: React.ReactNode) => (
              <ResolverLink
                resolverTag={
                  hasMaxBonus
                    ? ResolverTag.InstantBonusSplitwagerTermsCashCap
                    : ResolverTag.InstantBonusSplitwagerTermsCash
                }
              >
                {msg}
              </ResolverLink>
            )
          }}
        />
      </div>
    </>
  );
};

export default SplitWager;
